import booleanPointInPolygon from '@turf/boolean-point-in-polygon'
import { point } from '@turf/helpers'
import type * as GeoJSON from 'geojson'
import type { ContactLocation } from '../types/contactList'
import type { OverlayMode } from '../types/workplaceOverlay'
import type { AddressMode } from '../types/turf'

/**
 * Compute contacts within a polygon and return contact IDs + household count.
 * Reusable across MultiTurfDrawingMap, useTerraDraw, and daily-review editing.
 *
 * @param overlayMode - 'home' uses home coords, 'work' uses work coords,
 *   'hybrid' uses work if available else home.
 */
export function computeContactsInPolygon(
  contacts: ContactLocation[],
  polygon: GeoJSON.Feature<GeoJSON.Polygon>,
  overlayMode: OverlayMode = 'home'
): { contactIds: string[]; householdCount: number } {
  const contactsInPolygon = contacts.filter(contact => {
    let lng: number, lat: number
    if (overlayMode === 'work' && contact.workLongitude != null && contact.workLatitude != null) {
      lng = contact.workLongitude
      lat = contact.workLatitude
    } else if (overlayMode === 'hybrid') {
      // Prefer work location, fall back to home
      lng = contact.workLongitude ?? contact.longitude
      lat = contact.workLatitude ?? contact.latitude
    } else {
      lng = contact.longitude
      lat = contact.latitude
    }
    return booleanPointInPolygon(point([lng, lat]), polygon)
  })
  const householdKeys = new Set(
    contactsInPolygon
      .filter(c => c.streetAddress && c.zip)
      .map(c => `${c.streetAddress.trim().toUpperCase()}|${c.zip}`)
  )
  return {
    contactIds: contactsInPolygon.map(c => c.id),
    householdCount: householdKeys.size,
  }
}

/**
 * Compute bounding box from an array of polygons.
 * Returns [[minLng, minLat], [maxLng, maxLat]].
 */
export function computePolygonBounds(
  polygons: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>[]
): [[number, number], [number, number]] | null {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity
  let hasCoords = false

  for (const feature of polygons) {
    const coords = feature.geometry.type === 'Polygon'
      ? feature.geometry.coordinates[0]
      : feature.geometry.coordinates[0][0]
    for (const coord of coords) {
      minLng = Math.min(minLng, coord[0])
      minLat = Math.min(minLat, coord[1])
      maxLng = Math.max(maxLng, coord[0])
      maxLat = Math.max(maxLat, coord[1])
      hasCoords = true
    }
  }

  return hasCoords ? [[minLng, minLat], [maxLng, maxLat]] : null
}

/**
 * Map frontend OverlayMode ('home'|'work'|'hybrid') to backend AddressMode enum.
 */
export function overlayModeToAddressMode(mode: OverlayMode): AddressMode {
  const map: Record<OverlayMode, AddressMode> = { home: 'Home', work: 'Work', hybrid: 'Hybrid' }
  return map[mode]
}

/**
 * Map backend AddressMode enum to frontend OverlayMode.
 */
export function addressModeToOverlayMode(mode: AddressMode): OverlayMode {
  const map: Record<AddressMode, OverlayMode> = { Home: 'home', Work: 'work', Hybrid: 'hybrid' }
  return map[mode]
}
